/** Pure visibility-condition evaluator used by both designer preview and public renderer. */
import type { FormValues, VisibilityCondition } from '../models/form';

/** Resolves a `TabName.FieldName` value from the nested submission object. */
export function getValueByPath(values: FormValues, source: string): unknown {
  if (!source) return undefined;
  const normalized = source.replace(/^\$?scope\./, '').split('.').filter(Boolean);
  const parts = normalized.length > 2 ? normalized.slice(-2) : normalized;
  if (parts.length === 2) return values[parts[0] ?? '']?.[parts[1] ?? ''];

  for (const page of Object.values(values)) {
    if (Object.prototype.hasOwnProperty.call(page, source)) return page[source];
  }
  return undefined;
}

/** Evaluates a structured condition without `eval` or AngularJS expression parsing. */
export function evaluateVisibility(condition: VisibilityCondition | undefined, values: FormValues): boolean {
  if (!condition?.enabled) return true;
  const actual = getValueByPath(values, condition.source);
  const expected = condition.value ?? '';
  const actualString = String(actual ?? '');

  switch (condition.operator) {
    case '==': return actualString === expected;
    case '!=': return actualString !== expected;
    case 'contains': return actualString.includes(expected);
    case 'notContains': return !actualString.includes(expected);
    case '>': return Number(actualString) > Number(expected);
    case '>=': return Number(actualString) >= Number(expected);
    case '<': return Number(actualString) < Number(expected);
    case '<=': return Number(actualString) <= Number(expected);
    case 'isEmpty': return actual == null || actualString.trim() === '';
    case 'isNotEmpty': return actual != null && actualString.trim() !== '';
    default: return true;
  }
}
